import { Link, useLocation } from "react-router-dom";
import { ChevronLeft, Home } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

interface BreadcrumbItem {
  label: string;
  href: string;
}

// Route to Hebrew label mapping
const routeLabels: Record<string, string> = {
  "": "דף הבית",
  "destinations": "יעדים",
  "israel": "בארץ",
  "world": "בעולם",
  "north": "צפון",
  "center": "מרכז",
  "jerusalem": "ירושלים",
  "south": "דרום",
  "eilat": "אילת",
  "europe": "אירופה",
  "america": "אמריקה",
  "asia": "אסיה",
  "africa": "אפריקה",
  "australia": "אוסטרליה",
  "hotels": "מלונות",
  "flights": "טיסות",
  "cars": "השכרת רכב",
  "packages": "חבילות נופש",
  "esim": "eSIM",
  "insurance": "ביטוח נסיעות",
  "attractions": "אטרקציות",
  "tickets": "כרטיסים",
  "services": "שירותים",
  "business-content": "תוכן לעסקים",
  "about": "אודות",
  "contact": "צור קשר",
  "guides": "מדריכים",
  "maps": "מפות",
  "planning": "תכנון טיול",
  "deals": "דילים",
  "product": "מוצר",
  "terms": "תנאי שימוש",
  "privacy": "מדיניות פרטיות",
  "accessibility": "הצהרת נגישות",
  "tools": "כלים",
  "packing-list": "רשימת אריזה",
};

export const DynamicBreadcrumbs = () => {
  const location = useLocation();
  const pathSegments = location.pathname.split("/").filter(Boolean);

  // Don't show breadcrumbs on homepage
  if (pathSegments.length === 0) {
    return null;
  }

  // Build breadcrumb items
  const breadcrumbs: BreadcrumbItem[] = [
    { label: "דף הבית", href: "/" },
  ];

  let currentPath = "";
  pathSegments.forEach((segment) => {
    currentPath += `/${segment}`;
    const label = routeLabels[segment] || segment;
    breadcrumbs.push({
      label,
      href: currentPath,
    });
  });

  // Generate JSON-LD structured data
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.label,
      "item": `https://travel-boost.com${item.href}`,
    })),
  };

  return (
    <>
      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      {/* Breadcrumb Navigation */}
      <nav 
        className="bg-muted/50 border-b border-border/50 py-3"
        aria-label="ניווט פירורי לחם"
        dir="rtl"
      >
        <div className="container max-w-6xl">
          <Breadcrumb>
            <BreadcrumbList className="flex-row-reverse justify-end">
              {breadcrumbs.map((item, index) => {
                const isLast = index === breadcrumbs.length - 1;
                const isFirst = index === 0;

                return (
                  <BreadcrumbItem key={item.href}>
                    {/* Separator (not for first item) */}
                    {index > 0 && (
                      <BreadcrumbSeparator className="mx-1.5">
                        <ChevronLeft className="h-3.5 w-3.5 text-muted-foreground" />
                      </BreadcrumbSeparator>
                    )}

                    {isLast ? (
                      // Current page - bold, not clickable
                      <BreadcrumbPage className="font-bold text-foreground">
                        {item.label}
                      </BreadcrumbPage>
                    ) : (
                      // Previous pages - clickable links
                      <BreadcrumbLink asChild>
                        <Link
                          to={item.href}
                          className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-1 min-h-[44px] px-1"
                        >
                          {isFirst && <Home className="h-3.5 w-3.5" aria-hidden="true" />}
                          <span>{item.label}</span>
                        </Link>
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                );
              })}
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </nav>
    </>
  );
};